import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type PlanId = 'free' | 'subscriber' | 'enterprise';

export interface PlanLimits {
  twinsPerMonth: number;   // -1 = unlimited
  machines: number;
  sensorsPerMachine: number;
  retentionDays: number;
  mlPredictions: boolean;
}

export interface Plan {
  id: PlanId;
  name: string;
  price: number;
  priceLabel: string;
  color: string;
  limits: PlanLimits;
  features: string[];
}

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free', name: 'Free', price: 0, priceLabel: 'Free', color: '#60a5fa',
    limits: {
      twinsPerMonth: 1, machines: 5, sensorsPerMachine: 5,
      retentionDays: 7, mlPredictions: false,
    },
    features: ['1 twin per month', '5 machines', '5 sensors/machine', '7-day history'],
  },
  subscriber: {
    id: 'subscriber', name: 'Subscriber', price: 49, priceLabel: '$49/mo', color: '#a78bfa',
    limits: {
      twinsPerMonth: 5, machines: 25, sensorsPerMachine: 50,
      retentionDays: 90, mlPredictions: true,
    },
    features: ['5 twins per month', '25 machines', '50 sensors/machine', '90-day history', 'ML Predictions'],
  },
  enterprise: {
    id: 'enterprise', name: 'Enterprise', price: 0, priceLabel: 'Custom', color: '#34d399',
    limits: {
      twinsPerMonth: -1, machines: -1, sensorsPerMachine: -1,
      retentionDays: 365, mlPredictions: true,
    },
    features: ['Unlimited twins', 'Unlimited machines', 'Unlimited sensors', '365-day history', 'Dedicated support'],
  },
};

export interface Usage {
  twinsThisMonth: number;
  machines: number;
  totalSensors: number;
  apiCallsToday: number;
  storageGB: number;
  teamMembers: number;
}

export interface LimitCheck {
  allowed: boolean;
  reason?: string;
  pct?: number;
  isWarning: boolean;
  isBlocked: boolean;
}

interface PlanCtx {
  plan: Plan;
  usage: Usage;
  setPlanId: (id: PlanId) => void;
  checkTwinCreation: () => LimitCheck;
  checkMachines: () => LimitCheck;
  checkSensors: (machineId: string, currentCount: number) => LimitCheck;
  usagePct: (used: number, limit: number) => number;
  nextPlan: () => Plan | null;
}

const PlanContext = createContext<PlanCtx>({} as PlanCtx);

interface Props {
  children: React.ReactNode;
  machineCount: number;
  sensorCounts: Record<string, number>;
}

export const PlanProvider: React.FC<Props> = ({ children, machineCount, sensorCounts }) => {
  const [planId, setPlanIdState] = useState<PlanId>(() => {
    const saved = localStorage.getItem('cf_plan') as PlanId | null;
    return saved && PLANS[saved] ? saved : 'free';
  });

  // Re-sync plan from localStorage when it changes (e.g., after login)
  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem('cf_plan') as PlanId | null;
      const current = saved && PLANS[saved] ? saved : 'free';
      if (current !== planId) setPlanIdState(current);
    }, 1000);
    return () => clearInterval(interval);
  }, [planId]);

  const plan = PLANS[planId];

  // Track twins created this month
  const getTwinsThisMonth = (): number => {
    try {
      const data = JSON.parse(localStorage.getItem('cf_twins_month') || '{}');
      const now = new Date();
      const key = `${now.getFullYear()}-${now.getMonth()}`;
      return data[key] || 0;
    } catch { return 0; }
  };

  const usage: Usage = {
    twinsThisMonth: getTwinsThisMonth(),
    machines: machineCount,
    totalSensors: Object.values(sensorCounts).reduce((a, b) => a + b, 0),
    apiCallsToday: 0,
    storageGB: 0,
    teamMembers: 1,
  };

  const setPlanId = useCallback((id: PlanId) => {
    setPlanIdState(id);
    localStorage.setItem('cf_plan', id);
  }, []);

  const usagePct = (used: number, limit: number): number => {
    if (limit === -1) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const checkTwinCreation = useCallback((): LimitCheck => {
    const { twinsPerMonth: limit } = plan.limits;
    if (limit === -1) return { allowed: true, isWarning: false, isBlocked: false };
    const used = getTwinsThisMonth();
    const pct = usagePct(used, limit);
    if (used >= limit) return {
      allowed: false, isWarning: false, isBlocked: true, pct,
      reason: `Twin limit reached (${used}/${limit} this month). Upgrade to create more.`,
    };
    if (pct >= 80) return {
      allowed: true, isWarning: true, isBlocked: false, pct,
      reason: `${used}/${limit} twins used this month.`,
    };
    return { allowed: true, isWarning: false, isBlocked: false, pct };
  }, [plan]);

  const checkMachines = useCallback((): LimitCheck => {
    const { machines: limit } = plan.limits;
    if (limit === -1) return { allowed: true, isWarning: false, isBlocked: false };
    const pct = usagePct(usage.machines, limit);
    if (usage.machines >= limit) return {
      allowed: false, isWarning: false, isBlocked: true, pct,
      reason: `Machine limit reached (${usage.machines}/${limit}). Upgrade to add more.`,
    };
    if (pct >= 80) return {
      allowed: true, isWarning: true, isBlocked: false, pct,
      reason: `${usage.machines}/${limit} machines used.`,
    };
    return { allowed: true, isWarning: false, isBlocked: false, pct };
  }, [plan, usage.machines]);

  const checkSensors = useCallback((machineId: string, currentCount: number): LimitCheck => {
    const { sensorsPerMachine: limit } = plan.limits;
    if (limit === -1) return { allowed: true, isWarning: false, isBlocked: false };
    const pct = usagePct(currentCount, limit);
    if (currentCount >= limit) return {
      allowed: false, isWarning: false, isBlocked: true, pct,
      reason: `Sensor limit reached (${currentCount}/${limit} per machine). Upgrade to add more.`,
    };
    return { allowed: true, isWarning: false, isBlocked: false, pct };
  }, [plan]);

  const nextPlan = useCallback((): Plan | null => {
    const order: PlanId[] = ['free', 'subscriber', 'enterprise'];
    const idx = order.indexOf(planId);
    return idx < order.length - 1 ? PLANS[order[idx + 1]] : null;
  }, [planId]);

  return (
    <PlanContext.Provider value={{ plan, usage, setPlanId, checkTwinCreation, checkMachines, checkSensors, usagePct, nextPlan }}>
      {children}
    </PlanContext.Provider>
  );
};

export const usePlan = () => useContext(PlanContext);

// Helper: call after creating a twin to increment monthly count
export const incrementTwinCount = () => {
  try {
    const data = JSON.parse(localStorage.getItem('cf_twins_month') || '{}');
    const now = new Date();
    const key = `${now.getFullYear()}-${now.getMonth()}`;
    data[key] = (data[key] || 0) + 1;
    localStorage.setItem('cf_twins_month', JSON.stringify(data));
  } catch {}
};
